import { useMemo, useState, useEffect } from 'react'
import s from '../styles/ActivityCard.module.scss'
import { ReactComponent as CheckIcon } from 'assets/icons/check-circle.svg'

/**
 * Agent Activity Card
 * Visible while loading AND for a brief fade-out after loading completes.
 * Shows real-time tool steps from the backend.
 */
export default function ActivityCard({ isLoading, steps }) {
	// Keep the card visible for 1.5s after loading finishes so the user sees the steps
	const [visible, setVisible] = useState(false)
	const [fading, setFading] = useState(false)

	useEffect(() => {
		if (isLoading) {
			setVisible(true)
			setFading(false)
		} else if (visible) {
			// Start fade-out
			setFading(true)
			const timer = setTimeout(() => {
				setVisible(false)
				setFading(false)
			}, 1500)
			return () => clearTimeout(timer)
		}
	}, [isLoading]) // eslint-disable-line react-hooks/exhaustive-deps

	// Build display steps — hook must be called unconditionally (Rules of Hooks)
	const displaySteps = useMemo(() => {
		const source = (steps && steps.length > 0 ? steps : []) || []

		const real = source.map(st => ({
			label: st.label || st,
			status: st.status || 'done',
		}))

		// When done (fading), mark all as done
		if (!isLoading) {
			return [
				{ label: 'Processing request', status: 'done' },
				...real.map(r => ({ ...r, status: 'done' })),
			]
		}

		return [{ label: 'Processing request', status: 'done' }, ...real]
	}, [steps, isLoading])

	// Early return AFTER all hooks
	if (!visible) return null

	return (
		<div className={`${s.card} ${fading ? s.fadeOut : ''}`}>
			<div className={s.header}>
				<span className={s.statusIcon}>
					<CheckIcon />
				</span>
				<span className={s.statusText}>
					{isLoading ? 'IN PROGRESS' : 'COMPLETED'}
				</span>
			</div>

			<div className={s.steps}>
				<div className={s.stepsHeader}>
					<span className={s.stepsIcon}>
						<span className={s.spinner} />
					</span>
					<span className={s.stepsTitle}>ACTIVITY</span>
				</div>
				<div className={s.stepsDivider} />

				{displaySteps.map((step, i) => {
					const isDone = step.status === 'done'
					const isRunning = step.status === 'running'

					return (
						<div
							key={`${step.label}-${i}`}
							className={`${s.stepItem} ${isDone ? s.done : ''} ${isRunning ? s.current : ''}`}
						>
							<span className={s.stepIcon}>
								{isDone ? (
									<CheckIcon className={s.stepIcon__done} />
								) : isRunning ? (
									<span className={s.spinner} />
								) : (
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
										<circle
											cx="12"
											cy="12"
											r="10"
											stroke="#d4cde0"
											strokeWidth="2"
											fill="none"
										/>
									</svg>
								)}
							</span>
							<span className={s.stepLabel}>{step.label}</span>
						</div>
					)
				})}
			</div>
		</div>
	)
}
